import { BASE_URL } from '@/utils/api';
import { validateDocumentFile } from '@/utils/documentUploadLimits';
import { postFormWithProgress, type UploadOptions } from '@/utils/uploadWithProgress';
import type { CaseDocument } from './documentService';
import type {
  CasePaymentRecord,
  InvoiceDetail,
  InvoiceListItem,
  InvoiceStats,
  PaymentQr,
} from './billingService';
import type { CaseApproval } from './approvalService';
import type { CaseAcknowledgmentDocument } from './caseAcknowledgmentDocumentService';

// Client Portal auth - deliberately separate localStorage keys from the
// employee session ('token', 'employeeId', etc.) so a client login can never
// be picked up by the internal app's request helpers, and vice versa.

const TOKEN_KEY = 'clientPortalToken';
const CLIENT_ID_KEY = 'clientPortalClientId';
const CLIENT_NAME_KEY = 'clientPortalClientName';

export interface ClientPortalLoginResult {
  token: string;
  clientId: number;
  clientCode: string;
  clientName: string;
  username: string;
}

export interface ClientPortalMe {
  clientId: number;
  clientCode: string;
  clientName: string;
  businessName: string | null;
  email: string | null;
  mobile: string | null;
}

function getHeaders(): Record<string, string> {
  const token = typeof window !== 'undefined' ? localStorage.getItem(TOKEN_KEY) || '' : '';
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
}

function getAuthHeaderOnly(): Record<string, string> {
  const token = typeof window !== 'undefined' ? localStorage.getItem(TOKEN_KEY) || '' : '';
  return { Authorization: `Bearer ${token}` };
}

async function handleJsonResponse<T>(res: Response, fallbackMessage: string): Promise<T> {
  const result = await res.json().catch(() => ({}));
  if (!res.ok || result?.status === false) {
    throw new Error(result?.message || fallbackMessage);
  }
  return result.data;
}

export function getClientPortalToken(): string | null {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(TOKEN_KEY);
}

export function storeClientPortalSession(result: ClientPortalLoginResult) {
  localStorage.setItem(TOKEN_KEY, result.token);
  localStorage.setItem(CLIENT_ID_KEY, String(result.clientId));
  localStorage.setItem(CLIENT_NAME_KEY, result.clientName);
}

export function clearClientPortalSession() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(CLIENT_ID_KEY);
  localStorage.removeItem(CLIENT_NAME_KEY);
}

export async function fetchClientPortalMe(): Promise<ClientPortalMe> {
  const res = await fetch(`${BASE_URL}/api/client-portal/me`, { headers: getHeaders() });
  const result = await res.json().catch(() => ({}));
  if (!res.ok || result?.status === false) {
    throw new Error(result?.message || 'Failed to load your profile');
  }
  return result.data;
}

// Deliberately has no internal workflow-stage label text (see the backend
// DTO's own comment) - only currentStageNum, which ClientServiceStatus.tsx
// maps onto its own small client-facing stage set.
export interface ClientPortalServiceCase {
  caseId: number;
  caseCode: string;
  serviceName: string | null;
  financialYear: string;
  currentStageNum: number;
  status: string;
  dueDate: string | null;
  updatedAt: string;
  assignedEmployeeId: number | null;
  assignedEmployeeName: string | null;
  assignedEmployeeMobile: string | null;
}

export async function fetchMyServiceCases(): Promise<ClientPortalServiceCase[]> {
  const res = await fetch(`${BASE_URL}/api/client-portal/service-cases`, {
    headers: getHeaders(),
  });
  const result = await res.json().catch(() => ({}));
  if (!res.ok || result?.status === false) {
    throw new Error(result?.message || 'Failed to fetch your service cases');
  }
  return result.data || [];
}

// ---- Documents ----

export async function fetchMyDocuments(): Promise<CaseDocument[]> {
  const res = await fetch(`${BASE_URL}/api/client-portal/documents`, { headers: getHeaders() });
  return handleJsonResponse(res, 'Failed to fetch your documents');
}

export async function uploadMyDocument(
  documentId: number | string,
  file: File,
  options: UploadOptions = {}
): Promise<CaseDocument> {
  const invalid = validateDocumentFile(file);
  if (invalid) throw new Error(invalid);
  const form = new FormData();
  form.append('file', file);
  return postFormWithProgress<CaseDocument>(
    `${BASE_URL}/api/client-portal/documents/${documentId}/upload`,
    form,
    getAuthHeaderOnly(),
    'Failed to upload document',
    options
  );
}

// ---- Notifications (real bell - replaces the old mock Pending Actions banner) ----

export interface ClientPortalNotification {
  notificationId: number;
  notificationType: string;
  title: string;
  message: string;
  referenceId: number | null;
  linkUrl: string | null;
  isRead: boolean;
  createdAt: string;
}

export async function fetchMyNotifications(): Promise<ClientPortalNotification[]> {
  const res = await fetch(`${BASE_URL}/api/client-portal/notifications`, { headers: getHeaders() });
  const result = await res.json().catch(() => []);
  if (!res.ok) {
    throw new Error('Failed to fetch notifications');
  }
  return result;
}

export async function fetchMyUnreadNotificationCount(): Promise<number> {
  const res = await fetch(`${BASE_URL}/api/client-portal/notifications/unread-count`, {
    headers: getHeaders(),
  });
  const result = await res.json().catch(() => ({}));
  if (!res.ok || result?.status === false) {
    throw new Error(result?.message || 'Failed to fetch unread count');
  }
  return result.count || 0;
}

export async function markMyNotificationRead(notificationId: number): Promise<void> {
  await fetch(`${BASE_URL}/api/client-portal/notifications/${notificationId}/read`, {
    method: 'PUT',
    headers: getHeaders(),
  });
}

export async function markAllMyNotificationsRead(): Promise<void> {
  await fetch(`${BASE_URL}/api/client-portal/notifications/read-all`, {
    method: 'PUT',
    headers: getHeaders(),
  });
}

// ---- Billing (own invoices + pay-via-QR) ----

export async function fetchMyInvoices(): Promise<InvoiceListItem[]> {
  const res = await fetch(`${BASE_URL}/api/client-portal/invoices?size=100`, {
    headers: getHeaders(),
  });
  const result = await res.json().catch(() => ({}));
  if (!res.ok || result?.status === false) {
    throw new Error(result?.message || 'Failed to fetch your invoices');
  }
  return result.data || [];
}

export async function fetchMyInvoiceStats(): Promise<InvoiceStats> {
  const res = await fetch(`${BASE_URL}/api/client-portal/invoices/stats`, {
    headers: getHeaders(),
  });
  return handleJsonResponse(res, 'Failed to fetch your payment summary');
}

export async function fetchMyInvoiceDetail(caseId: number | string): Promise<InvoiceDetail> {
  const res = await fetch(`${BASE_URL}/api/client-portal/invoices/${caseId}`, {
    headers: getHeaders(),
  });
  return handleJsonResponse(res, 'Failed to fetch invoice detail');
}

export async function submitMyPayment(
  caseId: number | string,
  payload: { amount: number; referenceNumber: string; remarks?: string },
  proofFile?: File | null
): Promise<CasePaymentRecord> {
  const form = new FormData();
  form.append('payload', new Blob([JSON.stringify(payload)], { type: 'application/json' }));
  if (proofFile) form.append('proofFile', proofFile);
  const res = await fetch(`${BASE_URL}/api/client-portal/invoices/${caseId}/payments`, {
    method: 'POST',
    headers: getAuthHeaderOnly(),
    body: form,
  });
  return handleJsonResponse(res, 'Failed to submit payment');
}

export async function fetchMyPaymentQr(): Promise<PaymentQr> {
  const res = await fetch(`${BASE_URL}/api/client-portal/billing/qr`, { headers: getHeaders() });
  return handleJsonResponse(res, 'Could not load the payment details. Please try again.');
}

// ---- Certificates & Acknowledgments (documents Beyonli sent TO the client) ----

export async function fetchMySentDocuments(): Promise<CaseAcknowledgmentDocument[]> {
  const res = await fetch(`${BASE_URL}/api/client-portal/sent-documents`, {
    headers: getHeaders(),
  });
  return handleJsonResponse(res, 'Failed to fetch your documents');
}

// ---- Approvals ----

export async function fetchMyApprovals(): Promise<CaseApproval[]> {
  const res = await fetch(`${BASE_URL}/api/client-portal/approvals`, { headers: getHeaders() });
  return handleJsonResponse(res, 'Failed to fetch approvals');
}

export async function sendApprovalOtp(
  approvalId: number | string
): Promise<{ maskedEmail: string }> {
  const res = await fetch(`${BASE_URL}/api/client-portal/approvals/${approvalId}/send-otp`, {
    method: 'POST',
    headers: getHeaders(),
  });
  return handleJsonResponse(res, 'Failed to send OTP');
}

export async function verifyApprovalOtp(
  approvalId: number | string,
  otp: string
): Promise<CaseApproval> {
  const res = await fetch(`${BASE_URL}/api/client-portal/approvals/${approvalId}/verify-otp`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ otp }),
  });
  return handleJsonResponse(res, 'Failed to verify OTP');
}

export async function raiseApprovalQuery(
  approvalId: number | string,
  message: string
): Promise<CaseApproval> {
  const res = await fetch(`${BASE_URL}/api/client-portal/approvals/${approvalId}/raise-query`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ message }),
  });
  return handleJsonResponse(res, 'Failed to submit query');
}
